import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  MeetingConsentBody,
  MeetingConsentKey,
  MeetingGuestConsentBody,
  MeetingGuestState,
  MeetingMarkerBody,
  MeetingParticipantState,
  MeetingStatus,
  MeetingTaskBody,
  MeetingTranscriptStatus,
} from '@brimo/shared';
import { ApiError, api } from '../../lib/api';
import { queryRoots } from '../../lib/events';

/** Types and hooks of the video room (docs/dev/api-meetings.md): host routes, review, guest link. */

export interface MeetingParticipant {
  id: string;
  type: 'advisor' | 'guest';
  name: string;
  state: MeetingParticipantState;
  consentAt: string | null;
  waitingSince: string | null;
}

export interface MeetingOpenTask {
  id: string;
  title: string;
  dueLabel: string | null;
  done: boolean;
}

/** One item of the consent the meeting asks for (two without recording, four with it — in screen order). */
export interface MeetingConsentItem {
  key: MeetingConsentKey;
  label: string;
}

/**
 * The consent body for the items the server listed: every listed item confirmed; `recording` and `transcript`
 * are sent only when they were listed (a meeting that records).
 */
export function consentBodyFor(items: readonly MeetingConsentItem[]): MeetingConsentBody {
  const listed = new Set(items.map((item) => item.key));
  return {
    storage: listed.has('storage'),
    terms: listed.has('terms'),
    ...(listed.has('recording') ? { recording: true } : {}),
    ...(listed.has('transcript') ? { transcript: true } : {}),
  };
}

export interface MeetingDealContext {
  id: string;
  number: number;
  title: string | null;
  stage: string;
  purchasePrice: string | null;
  area: string | null;
  advisor: string | null;
}

export interface MeetingView {
  id: string;
  status: MeetingStatus;
  statusLabel: string;
  appointment: {
    id: string;
    eventTypeName: string;
    contactName: string | null;
    timePill: string;
    dealLine: string | null;
    dealStage: string | null;
  };
  consent: {
    items: MeetingConsentItem[];
    note: string;
    advisorConfirmed: boolean;
    advisorConfirmedAt: string | null;
  };
  participants: MeetingParticipant[];
  waitingCount: number;
  /**
   * `enabled`: the meeting records (its event type's option „Aufzeichnung und Transkript", D-162); `active`
   * while the recording runs.
   */
  recording: { enabled: boolean; active: boolean; startedAt: string | null };
  /** The conversation start — the clock of the call and of the markers, with or without recording. */
  startedAt: string | null;
  guestUrl: string | null;
  /** LiveKit is set up for this meeting (the room; the egress as well when it records). */
  livekitConfigured: boolean;
  reviewHref: string | null;
  context: {
    deal: MeetingDealContext | null;
    openTasks: MeetingOpenTask[];
    markedCount: number;
  };
}

export interface MeetingTask {
  id: string;
  title: string;
  assigneeShort: string;
  dueLabel: string | null;
  sourceLabel: string | null;
  visibleToCustomer: boolean;
  done: boolean;
}

export interface TranscriptLine {
  timeSeconds: number;
  timeLabel: string;
  speakerName: string | null;
  text: string;
  marked: boolean;
  taskTitle: string | null;
}

export interface MeetingReview {
  meetingId: string;
  status: MeetingStatus;
  /** The meeting's event type records („Aufzeichnung und Transkript bleiben intern." only then). */
  recordingEnabled: boolean;
  header: { eventTypeName: string; contactName: string | null; pill: string };
  recording: { fileId: string; durationSeconds: number | null } | null;
  chapters: Array<{ timeSeconds: number; timeLabel: string; label: string }>;
  transcript: {
    status: MeetingTranscriptStatus;
    statusText: string;
    lines: TranscriptLine[];
    markedCount: number;
  };
  summary: {
    status: 'draft' | 'approved';
    text: string;
    generated: boolean;
    approvedAt: string | null;
    approvedByShort: string | null;
  };
  tasks: MeetingTask[];
}

export interface MeetingMarker {
  id: string;
  timeSeconds: number;
  timeLabel: string;
  text: string | null;
  task: { id: string; title: string; dueOn: string | null; visibleToCustomer: boolean };
}

export interface RoomCredentials {
  url: string;
  token: string;
}

export const meetingKey = (id: string) => [...queryRoots.meetings, 'detail', id] as const;
export const reviewKey = (id: string) => [...queryRoots.meetings, 'review', id] as const;

export function useMeeting(id: string) {
  return useQuery({
    queryKey: meetingKey(id),
    queryFn: ({ signal }) => api<MeetingView>(`/meetings/${id}`, { signal }),
    retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 1,
  });
}

export function useReview(id: string, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: reviewKey(id),
    queryFn: ({ signal }) => api<MeetingReview>(`/meetings/${id}/review`, { signal }),
    enabled: options.enabled ?? true,
    retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 1,
  });
}

/** The host's actions in lobby and conversation; every answer that is a `MeetingView` replaces the cache. */
export function useMeetingActions(id: string) {
  const queryClient = useQueryClient();
  const base = `/meetings/${id}`;
  const store = (view: MeetingView) => queryClient.setQueryData(meetingKey(id), view);
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: queryRoots.meetings }),
      queryClient.invalidateQueries({ queryKey: queryRoots.tasks }),
      queryClient.invalidateQueries({ queryKey: queryRoots.appointments }),
    ]);
  return {
    open: useMutation({
      mutationFn: () => api<MeetingView>(`${base}/open`, { method: 'POST', body: {} }),
      onSuccess: store,
    }),
    consent: useMutation({
      mutationFn: (body: MeetingConsentBody) => api<MeetingView>(`${base}/consent`, { method: 'POST', body }),
      onSuccess: store,
    }),
    admit: useMutation({
      mutationFn: (participantId: string) =>
        api<MeetingView>(`${base}/participants/${participantId}/admit`, { method: 'POST', body: {} }),
      onSuccess: store,
    }),
    start: useMutation({
      mutationFn: () => api<MeetingView>(`${base}/start`, { method: 'POST', body: {} }),
      onSuccess: store,
    }),
    end: useMutation({
      mutationFn: () => api<MeetingView>(`${base}/end`, { method: 'POST', body: {} }),
      onSuccess: async (view) => {
        store(view);
        await refresh();
      },
    }),
    marker: useMutation({
      mutationFn: (body: MeetingMarkerBody) =>
        api<MeetingMarker>(`${base}/markers`, { method: 'POST', body }),
      onSuccess: refresh,
    }),
    task: useMutation({
      mutationFn: (body: MeetingTaskBody) => api<MeetingTask>(`${base}/tasks`, { method: 'POST', body }),
      onSuccess: refresh,
    }),
    note: useMutation({
      mutationFn: (text: string) => api<{ id: string }>(`${base}/note`, { method: 'POST', body: { text } }),
      onSuccess: () => queryClient.invalidateQueries({ queryKey: queryRoots.contact }),
    }),
    screenShare: useMutation({
      mutationFn: (body: { state: 'started' | 'ended'; label: string }) =>
        api<{ ok: true }>(`${base}/screen-share`, { method: 'POST', body }),
    }),
  };
}

/** Access data for the room client (`409` while LiveKit is not configured). */
export function fetchRoomToken(meetingId: string): Promise<RoomCredentials> {
  return api<RoomCredentials>(`/meetings/${meetingId}/token`, { method: 'POST', body: {} });
}

export function useReviewActions(id: string) {
  const queryClient = useQueryClient();
  const base = `/meetings/${id}`;
  const store = (review: MeetingReview) => queryClient.setQueryData(reviewKey(id), review);
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: reviewKey(id) }),
      queryClient.invalidateQueries({ queryKey: queryRoots.tasks }),
      queryClient.invalidateQueries({ queryKey: queryRoots.contact }),
    ]);
  return {
    save: useMutation({
      mutationFn: (text: string) =>
        api<MeetingReview>(`${base}/summary`, { method: 'PATCH', body: { text } }),
      onSuccess: store,
    }),
    approve: useMutation({
      mutationFn: (text: string) =>
        api<MeetingReview>(`${base}/summary/approve`, { method: 'POST', body: { text } }),
      onSuccess: async (review) => {
        store(review);
        await queryClient.invalidateQueries({ queryKey: queryRoots.contact });
      },
    }),
    visibility: useMutation({
      mutationFn: ({ taskId, visibleToCustomer }: { taskId: string; visibleToCustomer: boolean }) =>
        api<{ ok: true }>(`${base}/tasks/${taskId}`, { method: 'PATCH', body: { visibleToCustomer } }),
      onSuccess: refresh,
    }),
  };
}

export const exportHref = (id: string) => `/api/meetings/${id}/export.pdf`;
export const fileHref = (fileId: string) => `/api/files/${fileId}`;

// --- guest link (`/m/:token`) ---------------------------------------------------------------------------

export interface GuestView {
  state: MeetingGuestState;
  appointment: { eventTypeName: string; timeLabel: string; advisorName: string };
  organization: { name: string; imprintUrl: string | null; privacyUrl: string | null };
  /** The meeting records (consent intro, running head note and the declined page follow it). */
  recording: boolean;
  consentItems: MeetingConsentItem[];
  consentNote: string;
  guestName: string;
}

const guestBase = (token: string) => `/public/meetings/${encodeURIComponent(token)}`;

export const guestKey = (token: string) => ['public-meeting', token] as const;

export function useGuestView(token: string) {
  return useQuery({
    queryKey: guestKey(token),
    queryFn: ({ signal }) => api<GuestView>(guestBase(token), { signal }),
    retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 1,
    staleTime: 60_000,
  });
}

export function guestConsent(
  token: string,
  body: MeetingGuestConsentBody,
): Promise<{ accessKey: string; state: MeetingGuestState }> {
  return api(`${guestBase(token)}/consent`, { method: 'POST', body });
}

export function guestDecline(token: string): Promise<{ state: 'declined' }> {
  return api(`${guestBase(token)}/decline`, { method: 'POST', body: {} });
}

export function guestState(token: string, key: string): Promise<{ state: MeetingGuestState }> {
  return api(`${guestBase(token)}/state?key=${encodeURIComponent(key)}`);
}

export function guestRoomToken(token: string, key: string): Promise<RoomCredentials> {
  return api(`${guestBase(token)}/token`, { method: 'POST', body: { key } });
}
